import { container } from 'tsyringe'
import Vue from 'vue'
import {
  VuexModule,
  Module,
  Mutation,
  Action,
  getModule,
} from 'vuex-module-decorators'
import store from '@/app/ui/store'
import { COD_TRACKER_PAGINATION, Utils } from '@/app/infrastructures/misc'
import { CODTrackerPresenter } from '@/app/ui/presenters/CODTrackerPresenter'
import {
  CODTrackers,
  CODTracker,
  Metadata,
  CODTrackerDetail,
} from '@/domain/entities/CODTracker'
import { Pagination } from '@/domain/entities/Pagination'
import { OriginRoute } from '@/domain/entities/Route'

interface Dropdown {
  value: string
  label: string
}

export interface CODTrackerState {
  isLoading: boolean
  CODData: CODTracker[]
  CODPaginationData: Pagination
  CODMetadata: Metadata
  CODDetail: CODTrackerDetail
  originData: Dropdown[]
  isOriginLoading: boolean
  isExportLoading: boolean
}

@Module({ namespaced: true, store, name: 'CODTracker', dynamic: true })
class CODTrackerController extends VuexModule implements CODTrackerState {
  private presenter: CODTrackerPresenter = container.resolve(
    CODTrackerPresenter
  )
  public isLoading = false
  public CODData: CODTracker[] = []
  public CODPaginationData = new Pagination(1, COD_TRACKER_PAGINATION)
  public CODMetadata = new Metadata()
  public CODDetail = new CODTrackerDetail()
  public originData: Dropdown[] = []
  public isOriginLoading = false
  public isExportLoading = false
  public constant = {
    CODStatus: [
      {
        value: '',
        label: 'Semua',
      },
      {
        value: 'POD',
        label: 'POD',
      },
      {
        value: 'CODREJ',
        label: 'COD Rejected',
      },
      {
        value: 'DEX',
        label: 'DEX',
      },
      {
        value: 'DEL',
        label: 'DEL',
      },
    ],
  }

  @Action({ rawError: true })
  public getOriginList() {
    this.setOriginLoading(true)

    this.presenter
      .getOrigins()
      .then(res => {
        this.setOriginData(res)
      })
      .catch(error => {
        Vue.notify({
          title: 'Fetch Origin Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setOriginLoading(false)
      })
  }

  @Action({ rawError: true })
  public getCODTrackerList(
    params: Record<string, string | number | undefined>
  ) {
    this.setLoading(true)
    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )

    this.presenter
      .getCODTrackers(formattedParams)
      .then(res => {
        this.setCODData(res)
      })
      .catch(error => {
        Vue.notify({
          title: 'Fetch COD Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public getCODTrackerDetail(packageId: string) {
    this.setLoading(true)

    this.presenter
      .getCODDetail(packageId)
      .then(res => {
        this.setCODDetail(res)
      })
      .catch(error => {
        Vue.notify({
          title: 'Fetch COD Detail Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public exportDriverScoreReport(
    params: Record<string, string | number | undefined>
  ) {
    this.setExportLoading(true)

    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )

    this.presenter
      .exportCODReport(formattedParams)
      .then(url => {
        window.location.href = url
      })
      .catch(error => {
        Vue.notify({
          title: 'Export COD Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setExportLoading(false)
      })
  }

  @Mutation
  private setOriginData(origins: OriginRoute[]) {
    this.originData = origins
      .map(origin => ({
        value: origin.letterCode as string,
        label: origin.letterCode as string,
      }))
      .filter(
        (element, i, val) => val.findIndex(t => t.value === element.value) === i
      )
  }

  @Mutation
  private setCODData(CODs: CODTrackers) {
    this.CODPaginationData = CODs.pagination as Pagination
    this.CODData = CODs.data as CODTracker[]
    this.CODMetadata = CODs.metadata as Metadata
  }

  @Mutation
  private setCODDetail(COD: CODTrackerDetail) {
    this.CODDetail = COD
  }

  @Mutation
  private setLoading(bool: boolean) {
    this.isLoading = bool
  }

  @Mutation
  private setOriginLoading(bool: boolean) {
    this.isOriginLoading = bool
  }

  @Mutation
  private setExportLoading(bool: boolean) {
    this.isExportLoading = bool
  }

  @Mutation
  public addOrigin(origin: { label: string; value: string }) {
    this.originData = [...this.originData, origin]
  }
}

export default getModule(CODTrackerController)
